import { tAccountErrorCode } from 'src/providers/SocketsProvider/Sockets.types';
import { toAccountErrorCode } from './accountErrorCode';

/**
 * Maps backend error codes to i18n keys under the `chat/chat` namespace.
 * The actual translations live in `public/locales/{es,en,br}/chat/chat.json`
 * under the `whatsappDisconnect.*` group.
 */
export const WHATSAPP_DISCONNECT_TRANSLATION_KEYS: Record<
  tAccountErrorCode,
  string
> = {
  BANNED: 'whatsappDisconnect.banned',
  DEVICE_REMOVED: 'whatsappDisconnect.deviceRemoved',
  SESSION_INVALIDATED: 'whatsappDisconnect.sessionInvalidated',
  CONNECTION_TIMEOUT: 'whatsappDisconnect.connectionTimeout',
  RECOVERABLE_WHATSAPP_ERROR: 'whatsappDisconnect.recoverableWhatsappError',
  USER_LOGGED_OUT: 'whatsappDisconnect.userLoggedOut',
  PROVIDER_DELETED: 'whatsappDisconnect.providerDeleted',
  NO_ACCOUNT: 'whatsappDisconnect.noAccount',
  WHATSAPP_RESTRICTED: 'whatsappDisconnect.whatsappRestricted',
  UNKNOWN: 'whatsappDisconnect.unknown',
};

export type tWhatsAppDisconnectLabel = {
  /** i18n key under `chat/chat`; pass to `t(...)`. Null when no code is known. */
  translationKey: string | null;
  /** Raw provider message; used only when no error_code is available. */
  fallbackText: string | null;
};

/**
 * Resolves how the banner should label a WhatsApp Web disconnect.
 *
 * - When a code is present (including `UNKNOWN`), return its localized
 *   translation key — the banner calls `t(key)` to render the message in
 *   the user's language. The raw provider `message` is dropped here, since
 *   it is typically a technical English string (ChatDaddy) that would clash
 *   with localized UI for ES / PT users.
 * - When no code is present but a raw message is, surface the raw message
 *   as a last-resort fallback (better than an empty banner).
 * - When neither is present, return null and let the banner render its
 *   default channel-disconnected copy unchanged.
 */
export function resolveWhatsAppDisconnectLabel(
  errorCode: tAccountErrorCode | null | undefined,
  errorMessage: string | null | undefined,
): tWhatsAppDisconnectLabel | null {
  // A code this map does not know reads as UNKNOWN instead of an undefined key.
  const code = toAccountErrorCode(errorCode);
  if (code) {
    return {
      translationKey: WHATSAPP_DISCONNECT_TRANSLATION_KEYS[code],
      fallbackText: null,
    };
  }

  if (errorMessage) {
    return {
      translationKey: null,
      fallbackText: errorMessage,
    };
  }

  return null;
}
